import { type HTMLAttributes } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/shared/lib/utils'
import { FavoriteButton } from '@/features/favorites/ui'
import { formatPrice } from '@/shared/lib/format'
import { preloadOptimizedImage } from '@/shared/lib/image-optimization'
import { OptimizedImage } from '@/shared/ui/optimized-image'
import type { Tea, Teaware } from '../model/types'

// Универсальный тип для карточки продукта
type Product = Tea | Teaware

// Хелпер для получения URL первого изображения
function getMainImageUrl(images: { url: string }[]): string {
  return images?.[0]?.url || '/placeholder-product.svg'
}

export interface ProductCardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Продукт для отображения
   */
  product: Product
  /**
   * В избранном ли товар (для обратной совместимости)
   * @deprecated Используйте productId для автоматического определения через FavoriteButton
   */
  isFavorite?: boolean
  /**
   * Обработчик клика на карточку
   * Вызывается при клике на всю карточку (переход на страницу товара)
   */
  onClick?: () => void
  /**
   * Обработчик добавления в избранное (для обратной совместимости)
   * @deprecated Используйте FavoriteButton который сам управляет состоянием
   */
  onFavoriteClick?: () => void
  /**
   * Обработчик добавления в корзину
   * Вызывается при клике на кнопку "В корзину"
   */
  onAddToCart?: () => void
  /**
   * Показывать кнопку добавления в корзину
   * @default true
   */
  showAddToCart?: boolean
  /**
   * Показывать кнопку избранного
   * @default true
   */
  showFavorite?: boolean
  /**
   * Состояние загрузки
   * При включении блокирует все взаимодействия и показывает состояние загрузки
   * @default false
   */
  loading?: boolean
  /**
   * Callback для haptic feedback при взаимодействии
   * Вызывается при клике на карточку или добавление в корзину
   */
  onHapticFeedback?: (type?: 'light' | 'medium') => void
}

/**
 * UI компонент: Карточка товара
 * 
 * Компонент для отображения карточки товара в каталоге.
 * Использует унифицированный FavoriteButton для управления избранным.
 * 
 * @example
 * ```tsx
 * <ProductCard
 *   product={product}
 *   onAddToCart={handleAddToCart}
 *   onClick={() => navigate(`/product/${product.id}`)}
 * />
 * ```
 */
export function ProductCard({
  product,
  isFavorite = false,
  onClick,
  onFavoriteClick,
  onAddToCart,
  showAddToCart = true,
  showFavorite = true,
  loading = false,
  onHapticFeedback,
  className,
  ...props
}: ProductCardProps) {
  const handleCardClick = () => {
    if (loading) return
    onHapticFeedback?.('light')
    onClick?.()
  }

  const handleCardPreview = () => {
    preloadOptimizedImage(mainImage, { preset: 'hero' })
  }

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (loading) return
    onHapticFeedback?.('medium')
    onAddToCart?.()
  }

  const mainImage = getMainImageUrl(product.images)
  const isTea = 'brewing' in product

  return (
    <motion.div
      className={cn(
        'bg-white dark:bg-dark-card rounded-[16px] overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.2)]',
        'border border-gray-200 dark:border-gray-700',
        'transition-all duration-300',
        'w-full min-w-0',
        {
          'cursor-pointer hover:shadow-[0_4px_16px_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_8px_24px_rgba(0,0,0,0.4),0_4px_8px_rgba(0,0,0,0.3)]': onClick && !loading,
          'opacity-60 pointer-events-none': !product.isInStock || loading,
        },
        className
      )}
      onClick={handleCardClick}
      onPointerEnter={handleCardPreview}
      onTouchStart={handleCardPreview}
      whileTap={onClick && !loading ? { scale: 0.98 } : undefined}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      {...(props as any)}
    >
      {/* Изображение */}
      <div className="relative aspect-square bg-[#EDE5D6] dark:bg-[#231712] overflow-hidden rounded-[16px]">
        <OptimizedImage
          src={mainImage}
          alt={product.name}
          preset="card"
          wrapperClassName="absolute inset-0"
          className="object-cover"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />

        {/* Overlay для "Нет в наличии" */}
        {!product.isInStock && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-white font-semibold text-sm">Нет в наличии</span>
          </div>
        )}

        {/* Кнопка избранного */}
        {showFavorite && (
          <div className="absolute top-2 right-2">
            <FavoriteButton
              productId={product.id}
              variant="card"
              size="md"
              animated
              onToggle={onFavoriteClick ? () => onFavoriteClick() : undefined}
            />
          </div>
        )}

        {/* Бейдж "Новинка" */}
        {product.isNew && (
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-primary/90 dark:bg-primary-dark/90 backdrop-blur-sm rounded-full text-xs font-medium text-white">
            Новинка
          </div>
        )}
      </div>

      {/* Контент */}
      <div className="p-3 space-y-2.5">
        {/* Название */}
        <div>
          <h3 className="text-sm font-semibold text-ink dark:text-white line-clamp-1 mb-1">
            {product.name}
          </h3>
          {product.categoryName && (
            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
              {product.categoryName}
            </p>
          )}
        </div>
        
        {/* Цена */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-baseline gap-1.5">
            {isTea ? (
              product.isOnSale && product.salePriceAmount ? (
                <>
                  <p className="text-base font-semibold text-primary dark:text-primary-light">
                    {formatPrice((product.salePriceAmount || 0) * 10)}
                  </p>
                  <p className="text-xs text-gray-400 line-through">
                    {formatPrice((product.priceAmount || 0) * 10)}
                  </p>
                </>
              ) : (
                <p className="text-base font-semibold text-primary dark:text-primary-light">
                  {formatPrice((product.priceAmount || 0) * 10)}
                </p>
              )
            ) : (
              product.isOnSale && product.salePriceFormatted ? (
                <>
                  <p className="text-base font-semibold text-primary dark:text-primary-light">
                    {product.salePriceFormatted}
                  </p>
                  <p className="text-xs text-gray-400 line-through">
                    {product.priceFormatted}
                  </p>
                </>
              ) : (
                <p className="text-base font-semibold text-primary dark:text-primary-light">
                  {product.priceFormatted}
                </p>
              )
            )}
            {isTea && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                за 10г
              </span>
            )}
          </div>
        </div>
        
        {/* Кнопка добавления в корзину */}
        {showAddToCart && onAddToCart && product.isInStock && (
          <button
            type="button"
            onClick={handleAddToCart}
            className={cn(
              'w-full py-2 px-4 rounded-lg',
              'bg-primary dark:bg-primary-dark text-white font-medium text-sm',
              'transition-all duration-150',
              'hover:bg-primary-dark dark:hover:bg-primary-darker active:scale-95',
              'touch-target'
            )}
          >
            В корзину
          </button>
        )}
      </div>
    </motion.div>
  )
}

export interface ProductImageViewProps extends HTMLAttributes<HTMLImageElement> {
  /**
   * URL изображения товара
   */
  src: string
  /**
   * Alt текст для изображения (для accessibility)
   */
  alt: string
  /**
   * Размер изображения
   * - `sm`: маленький (64x64px)
   * - `md`: средний (96x96px)
   * - `lg`: большой (128x128px)
   * - `full`: на всю ширину контейнера
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'full'
}

/**
 * UI компонент: Изображение товара
 * 
 * Компонент для отображения изображения товара с lazy loading.
 * 
 * @example
 * ```tsx
 * <ProductImageView
 *   src={product.images[0]}
 *   alt={product.name}
 *   size="md"
 * />
 * ```
 */
export function ProductImageView({
  src,
  alt,
  size = 'md',
  className,
  ...props
}: ProductImageViewProps) {
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
    full: 'w-full',
  }

  const imageSrc = src || '/placeholder-product.svg'

  return (
    <OptimizedImage
      src={imageSrc}
      alt={alt}
      preset={size === 'full' ? 'hero' : 'thumb'}
      wrapperClassName={cn(
        'rounded-lg bg-[#EDE5D6] dark:bg-[#231712]',
        sizeClasses[size],
        className
      )}
      className="object-cover"
      loading="lazy"
      {...props}
    />
  )
}

/** @deprecated Use ProductImageView instead */
export const ProductImage = ProductImageView

// Экспорт TeaCard
export { TeaCard, type TeaCardProps } from './tea-card'

// Экспорт SetCard (подарочные наборы)
export { SetCard, type SetCardProps } from './set-card'
